import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { notificationService } from '../services/notificationService';
import type { AppNotification } from '../services/notificationService';

function normalizeError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  return new Error(fallbackMessage);
}

function safelyUnsubscribe(unsubscribe: () => void, label: string) {
  try {
    // The service's void cleanup wrapper can return Supabase's promise at
    // runtime, so observe it instead of allowing a rejected cleanup to leak.
    const result = (unsubscribe as unknown as () => unknown)();
    void Promise.resolve(result).catch((error) => {
      console.error(`${label}:`, normalizeError(error, 'Subscription cleanup failed').message);
    });
  } catch (error) {
    console.error(`${label}:`, normalizeError(error, 'Subscription cleanup failed').message);
  }
}

export function useNotifications() {
  const [userId, setUserId] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Loading includes auth resolution so consumers never mistake an unresolved
  // user for a successfully loaded empty notification feed.
  useEffect(() => {
    let cancelled = false;

    const resolveUser = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;

        if (!user) {
          if (!cancelled) setIsLoading(false);
          return;
        }

        if (!cancelled) {
          setError(null);
          setUserId(user.id);
        }
      } catch (cause) {
        if (cancelled) return;
        const nextError = normalizeError(cause, 'Could not resolve the current user.');
        console.error('useNotifications: user resolution error:', nextError.message);
        setError(nextError);
        setIsLoading(false);
      }
    };

    void resolveUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let pendingLoads = 0;

    const load = async () => {
      if (cancelled) return;
      pendingLoads += 1;
      setIsLoading(true);
      setError(null);

      try {
        const [data, nextUnreadCount] = await Promise.all([
          notificationService.fetchNotifications(userId),
          notificationService.fetchUnreadCount(userId),
        ]);
        if (!cancelled) {
          setNotifications(data);
          setUnreadCount(nextUnreadCount);
        }
      } catch (cause) {
        if (!cancelled) {
          const nextError = normalizeError(cause, 'Could not load notifications.');
          console.error('useNotifications: load error:', nextError.message);
          setError(nextError);
          // Do not clear notifications when a realtime refresh fails.
        }
      } finally {
        pendingLoads -= 1;
        if (!cancelled && pendingLoads === 0) setIsLoading(false);
      }
    };

    void load();
    const unsubscribe = notificationService.subscribe(userId, () => {
      void load();
    });

    return () => {
      cancelled = true;
      safelyUnsubscribe(unsubscribe, 'useNotifications: unsubscribe error');
    };
  }, [userId]);

  const markRead = async (ids: string[]) => {
    try {
      await notificationService.markRead(ids);
      const newlyReadCount = notifications.filter((item) => ids.includes(item.id) && !item.read_at).length;
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((current) => Math.max(0, current - newlyReadCount));
      setError(null);
    } catch (cause) {
      const nextError = normalizeError(cause, 'Could not mark notifications as read.');
      console.error('useNotifications: markRead error:', nextError.message);
      setError(nextError);
    }
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead();
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
      setUnreadCount(0);
      setError(null);
    } catch (cause) {
      const nextError = normalizeError(cause, 'Could not mark all notifications as read.');
      console.error('useNotifications: markAllRead error:', nextError.message);
      setError(nextError);
    }
  };

  return { notifications, unreadCount, markRead, markAllRead, isLoading, error };
}
